// Feedback while a printout is being prepared.
//
// Rasterizing an A4 sheet takes long enough — fonts, images, html2canvas — that
// a button which only disables itself reads as "nothing happened". This is the
// one implementation both prescription print surfaces use, so they cannot give
// the user different signals for the same operation.
//
// Non-blocking by design: it never covers the page or steals focus, and it
// disappears on its own when the print dialog opens.

export function PrintProgressToast({
  message,
  error,
  onDismiss,
}: {
  /** What is happening right now, e.g. "Preparing Prescription…". */
  message: string;
  /** When set, the toast reports a problem instead of progress. */
  error?: string | null;
  /** Shown only for the error state — progress dismisses itself. */
  onDismiss?: () => void;
}) {
  const isError = Boolean(error);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 'var(--radius-full)',
        background: isError ? 'var(--color-danger, #dc2626)' : 'var(--text)',
        color: '#fff',
        fontSize: 'var(--font-size-sm)',
        boxShadow: '0 6px 20px rgba(15,23,42,0.25)',
        maxWidth: 'min(480px, 90vw)',
      }}
    >
      {!isError && (
        <>
          <style>{`@keyframes _print_toast_spin { to { transform: rotate(360deg) } }`}</style>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            style={{ animation: '_print_toast_spin 1s linear infinite', flexShrink: 0 }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </>
      )}
      <span>{error ?? message}</span>
      {isError && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.2)',
            color: '#fff',
            borderRadius: 'var(--radius-sm)',
            padding: '3px 10px',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
